import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

const STOP_RADIUS_M = 60;
const STOP_MIN_MS = 10 * 60 * 1000;
const GOOGLE_MAPS_API_KEY = "AIzaSyBIBvZPPT7MjD-UCGGUJdgPGNOTdAZmatQ";

const reverseGeocode = async (lat, lng) => {
  try {
    const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`);
    const j = await r.json();
    return j.results?.[0]?.formatted_address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
};

const pinIcon = (color, label) =>
  L.divIcon({
    className: "",
    html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,0.35);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:11px;font-family:'DM Sans',sans-serif"><div style="transform:rotate(45deg)">${label}</div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });

const startIcon = pinIcon("#10B981", "S");
const endIcon = pinIcon("#EF4444", "E");
const numberedStopIcon = (n) =>
  L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50%;background:#F59E0B;border:2px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,0.35);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:13px;font-family:'DM Sans',sans-serif">${n}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

const distanceMeters = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const detectStops = (points) => {
  const stops = [];
  let i = 0;
  while (i < points.length) {
    let j = i + 1;
    while (j < points.length && distanceMeters(points[i], points[j]) <= STOP_RADIUS_M) j++;
    const start = points[i];
    const end = points[j - 1];
    const duration = end.tsMs - start.tsMs;
    if (j - i >= 2 && duration >= STOP_MIN_MS) {
      const avgLat = points.slice(i, j).reduce((s, p) => s + p.lat, 0) / (j - i);
      const avgLng = points.slice(i, j).reduce((s, p) => s + p.lng, 0) / (j - i);
      stops.push({ lat: avgLat, lng: avgLng, startMs: start.tsMs, endMs: end.tsMs, durationMs: duration });
      i = j;
    } else {
      i++;
    }
  }
  return stops;
};

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30] });
  }, [bounds, map]);
  return null;
}

const fmtClock = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const fmtDuration = (ms) => {
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

export default function TrailMap({ db, entry, height = 420 }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stopAddresses, setStopAddresses] = useState({});

  useEffect(() => {
    if (!db || !entry?.id) return;
    const q = query(collection(db, "timeEntries", entry.id, "track"), orderBy("ts", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const v = d.data();
          const tsMs = v.ts?.toMillis ? v.ts.toMillis() : (v.ts?.seconds ? v.ts.seconds * 1000 : Date.now());
          return { lat: v.lat, lng: v.lng, accuracy: v.accuracy, tsMs };
        });
        setPoints(rows);
        setLoading(false);
      },
      (err) => {
        console.error("[TrailMap] snapshot error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [db, entry?.id]);

  const startPt = useMemo(() => (
    entry?.locationIn?.lat != null
      ? { lat: entry.locationIn.lat, lng: entry.locationIn.lng }
      : points[0]
  ), [entry?.locationIn?.lat, entry?.locationIn?.lng, points]);

  const endPt = useMemo(() => (
    entry?.locationOut?.lat != null
      ? { lat: entry.locationOut.lat, lng: entry.locationOut.lng }
      : points[points.length - 1]
  ), [entry?.locationOut?.lat, entry?.locationOut?.lng, points]);

  const polyline = useMemo(() => points.map((p) => [p.lat, p.lng]), [points]);
  const stops = useMemo(() => detectStops(points), [points]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pending = stops.filter((s) => !stopAddresses[`${s.lat},${s.lng}`]);
      if (!pending.length) return;
      const updates = {};
      for (const s of pending) {
        const key = `${s.lat},${s.lng}`;
        // eslint-disable-next-line no-await-in-loop
        updates[key] = await reverseGeocode(s.lat, s.lng);
        if (cancelled) return;
      }
      setStopAddresses((prev) => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [stops, stopAddresses]);

  const bounds = useMemo(() => {
    const all = [];
    if (startPt) all.push([startPt.lat, startPt.lng]);
    if (endPt) all.push([endPt.lat, endPt.lng]);
    polyline.forEach((p) => all.push(p));
    if (!all.length) return null;
    return L.latLngBounds(all);
  }, [startPt, endPt, polyline]);

  const center = startPt || (points[0] ? { lat: points[0].lat, lng: points[0].lng } : { lat: 35.4676, lng: -97.5164 });

  return (
    <div style={{ position: "relative" }}>
      <div style={{ height, borderRadius: 12, overflow: "hidden", border: "1px solid #E5E7EB" }}>
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={14}
          scrollWheelZoom
          style={{ height: "100%", width: "100%", background: "#F1F5F9" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" subdomains="abc" detectRetina={true} attribution="&copy; OpenStreetMap contributors" />
          {polyline.length > 1 && (
            <Polyline positions={polyline} pathOptions={{ color: "#059669", weight: 5, opacity: 0.9 }} />
          )}
          {startPt && (
            <Marker position={[startPt.lat, startPt.lng]} icon={startIcon}>
              <Popup>
                <b>Clock In</b>
                <br />
                {entry?.locationIn?.label || "Start"}
              </Popup>
            </Marker>
          )}
          {endPt && entry?.locationOut && (
            <Marker position={[endPt.lat, endPt.lng]} icon={endIcon}>
              <Popup>
                <b>Clock Out</b>
                <br />
                {entry?.locationOut?.label || "End"}
              </Popup>
            </Marker>
          )}
          {stops.map((s, i) => (
            <Marker key={i} position={[s.lat, s.lng]} icon={numberedStopIcon(i + 1)}>
              <Popup>
                <b>Stop {i + 1}</b>
                <br />
                {fmtClock(s.startMs)} – {fmtClock(s.endMs)} ({fmtDuration(s.durationMs)})
                <br />
                {stopAddresses[`${s.lat},${s.lng}`] || "Loading address…"}
              </Popup>
            </Marker>
          ))}
          {bounds && <FitBounds bounds={bounds} />}
        </MapContainer>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 10, fontSize: 11, color: "#64748B", flexWrap: "wrap" }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#10B981", marginRight: 5, verticalAlign: "middle" }} />Start</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#EF4444", marginRight: 5, verticalAlign: "middle" }} />End</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#F59E0B", marginRight: 5, verticalAlign: "middle" }} />Stop &gt;10 min</span>
        <span style={{ marginLeft: "auto" }}>
          {loading ? "Loading trail…" : `${points.length} GPS points · ${stops.length} stops`}
        </span>
      </div>
      {stops.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ color: "#64748B", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
            Stops ({stops.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {stops.map((s, i) => {
              const key = `${s.lat},${s.lng}`;
              const addr = stopAddresses[key];
              return (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 12px", background: "#F7F8FA", borderRadius: 10, border: "1px solid #E5E7EB" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#F59E0B", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#0F172A", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {addr || "Loading address…"}
                    </div>
                    <div style={{ color: "#64748B", fontSize: 11, marginTop: 2 }}>
                      {fmtClock(s.startMs)} – {fmtClock(s.endMs)}
                    </div>
                  </div>
                  <div style={{ color: "#70C0A8", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                    {fmtDuration(s.durationMs)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
